import { useState, useEffect, useRef } from 'react';
import { motion, animate } from 'framer-motion';

/**
 * Generates a set of points that resemble a random walk.
 * @param {number} width - The width of the drawing area.
 * @param {number} height - The height of the drawing area.
 * @param {number} numPoints - The number of points to generate.
 * @returns {Array<[number, number]>} An array of [x, y] coordinates.
 */
const generatePoints = (width, height, numPoints) => {
    const points = [];
    if (width <= 0 || height <= 0 || numPoints < 2) {
        return [[0, 0], [width, height / 2]]; // Return a simple fallback path
    }
    const segmentWidth = width / (numPoints - 1);
    const verticalCenter = height / 2;
    const amplitude = height / 2.5; // Controls the vertical spread
    let lastY = verticalCenter;

    for (let i = 0; i < numPoints; i++) {
        const x = i * segmentWidth;

        // Generate a new y-value based on the previous one to create a smoother random walk
        const randomStep = (Math.random() - 0.5) * (amplitude / 1.5);
        let newY = lastY + randomStep;

        // Add a "mean reversion" pull to keep the line centered vertically
        const pullToCenter = (verticalCenter - newY) * 0.05;
        newY += pullToCenter;

        // Clamp the values to prevent them from going too far off-screen
        if (newY > verticalCenter + amplitude) newY = verticalCenter + amplitude;
        if (newY < verticalCenter - amplitude) newY = verticalCenter - amplitude;

        points.push([x, newY]);
        lastY = newY;
    }
    return points;
};

/**
 * Creates an SVG path data string from an array of points using a Catmull-Rom spline.
 * This ensures the line passing through the points is smooth.
 * @param {Array<[number, number]>} data - The array of points.
 * @returns {string} The SVG 'd' attribute string.
 */
const createSvgPath = (data) => {
    if (data.length < 2) return "";

    let path = `M${data[0][0]},${data[0][1]}`;
    for (let i = 0; i < data.length - 1; i++) {
        const p0 = data[i === 0 ? 0 : i - 1];
        const p1 = data[i];
        const p2 = data[i + 1];
        const p3 = data[i + 2 > data.length - 1 ? i + 1 : i + 2];

        // Convert Catmull-Rom points to Cubic Bezier control points
        const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
        const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
        const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
        const cp2y = p2[1] - (p3[1] - p1[1]) / 6;

        path += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
    }
    return path;
};

/**
 * The main component that renders the animated Gaussian process loop.
 */
export default function App() {
    const pathRef = useRef(null);
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const numPoints = 15; // Fewer points create a smoother, broader curve

    // Effect to update dimensions based on the container size
    useEffect(() => {
        if (!containerRef.current) return;

        // Function to update state with current dimensions
        const updateDimensions = () => {
            setDimensions({
                width: containerRef.current.offsetWidth,
                height: containerRef.current.offsetHeight
            });
        };

        // Initialize dimensions
        updateDimensions();

        // Set up ResizeObserver to handle dimension changes
        const observer = new ResizeObserver(entries => {
            if (entries[0]) {
                const { width, height } = entries[0].contentRect;
                setDimensions({ width, height });
            }
        });
        observer.observe(containerRef.current);

        // Clean up the observer on component unmount
        return () => {
            observer.disconnect();
        };
    }, []);

    // Effect for the animation loop, dependent on dimensions
    useEffect(() => {
        const path = pathRef.current;
        const { width, height } = dimensions;

        if (!path || width === 0 || height === 0) return;

        // This async function runs an infinite loop to perpetually update the path
        const animationLoop = async () => {
            // Set the initial path without animation
            path.setAttribute('d', createSvgPath(generatePoints(width, height, numPoints)));

            // Infinite animation loop
            while (true) {
                const newPoints = generatePoints(width, height, numPoints);
                const newPathD = createSvgPath(newPoints);

                // Use framer-motion's animate function to smoothly transition to the new path
                await animate(path, { d: newPathD }, {
                    duration: 2.0,
                    type: "spring",
                    stiffness: 30,
                    damping: 15,
                });
            }
        };

        animationLoop();

    }, [dimensions, numPoints]); // The loop depends on the dynamic dimensions

    return (
        <div ref={containerRef} className="w-full min-h-[200px] rounded-lg overflow-hidden relative">
            <svg width="100%" height="100%" viewBox={`0 0 ${dimensions.width} ${dimensions.height}`} preserveAspectRatio="xMidYMid slice" className="absolute top-0 left-0">
                <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#4840d2" />
                        <stop offset="100%" stopColor="#38bdf8" />
                    </linearGradient>
                </defs>
                <motion.path
                    ref={pathRef}
                    fill="none"
                    stroke="url(#gradient)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );
}
